import { createContext, useContext, useEffect, useState } from "react";
import ApiClient, { ApiError } from "../api/ApiClient";
import { AuthAPIError, AuthAPIErrorType } from "../models/errors/AuthAPIError";
import {
  SignInResponse,
  RefreshTokenResponse,
} from "../models/schema/AuthAPIResponses";
import { useNavigate } from "react-router-dom";
import { LocalStorageKeys } from "../models/schema/LocalStorageKeys";

interface AuthContextType {
  isAuthenticated: boolean;
  accessToken: string | null;
  userPermissions: string | null;
  isLoading: boolean;
  error: string | null;
  handleSignIn: (email: string, password: string) => Promise<void>;
  handleSignOut: () => Promise<void>;
  handleRefreshToken: () => Promise<string | null>;
  handleError: (error: ApiError) => Promise<string | null>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: React.ReactNode;
}

const basePath = "/auth";

const AuthAPIPaths = {
  SignIn: basePath + "/sign-in",
  SignOut: basePath + "/sign-out",
  Refresh: basePath + "/refresh-token",
};

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [userPermissions, setUserPermissions] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  // Create ApiClient instance directly to avoid circular dependency
  const apiClient = new ApiClient(undefined, {
    getAccessToken: () => accessToken,
    handleAuthError: async (error: ApiError): Promise<string | null> => {
      return await handleError(error);
    },
    handleNationBuilderTokenError: async (error: ApiError): Promise<void> => {
      return Promise.resolve();
    },
  });

  useEffect(() => {
    const accessToken = localStorage.getItem(LocalStorageKeys.AuthToken);
    const userPermissions = localStorage.getItem(LocalStorageKeys.Groups);
    if (accessToken && userPermissions) {
      setAccessToken(accessToken);
      setUserPermissions(userPermissions);
      setIsAuthenticated(true);
    }
  }, []);

  const handleSignIn = async (email: string, password: string) => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await apiClient.post<SignInResponse>(
        AuthAPIPaths.SignIn,
        {
          email: email,
          password: password,
        },
      );
      setAccessToken(response.data.access_token);
      setUserPermissions(response.data.groups);
      localStorage.setItem(
        LocalStorageKeys.AuthToken,
        response.data.access_token,
      );
      localStorage.setItem(LocalStorageKeys.Groups, response.data.groups);
      setIsAuthenticated(true);
    } catch (error) {
      const apiError = error as ApiError;
      setError(apiError.data?.detail.message || apiError.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSignOut = async () => {
    setIsLoading(true);
    setError(null);

    try {
      await apiClient.post(AuthAPIPaths.SignOut);
      setAccessToken(null);
      setUserPermissions(null);
      localStorage.removeItem(LocalStorageKeys.AuthToken);
      localStorage.removeItem(LocalStorageKeys.Groups);
      setIsAuthenticated(false);
      navigate("/");
    } catch (error) {
      const apiError = error as ApiError;
      setError(apiError.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleRefreshToken = async (): Promise<string | null> => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await apiClient.post<RefreshTokenResponse>(
        AuthAPIPaths.Refresh,
      );
      if (response.data.access_token) {
        setAccessToken(response.data.access_token);
      }
      localStorage.setItem(
        LocalStorageKeys.AuthToken,
        response.data.access_token,
      );
      return response.data.access_token;
    } catch (error) {
      const apiError = error as ApiError;
      setError(apiError.message);
      return null;
    } finally {
      setIsLoading(false);
    }
  };

  const handleError = async (error: ApiError): Promise<string | null> => {
    const apiError = error.data as unknown as AuthAPIError;
    if (apiError.type === AuthAPIErrorType.NOT_LOGGED_IN) {
      setAccessToken(null);
      setUserPermissions(null);
      setIsLoading(false);
      setError(null);
      localStorage.removeItem(LocalStorageKeys.AuthToken);
      localStorage.removeItem(LocalStorageKeys.Groups);
      setIsAuthenticated(false);
      navigate("/");
      return null;
    } else if (apiError.type === AuthAPIErrorType.PERMISSION_DENIED) {
      setError(apiError.message);
      navigate("/");
      return null;
    } else if (apiError.type === AuthAPIErrorType.TOKEN_EXPIRED) {
      const newAccessToken = await handleRefreshToken();
      return newAccessToken;
    } else {
      setError(apiError.message);
      return null;
    }
  };

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        accessToken,
        userPermissions,
        isLoading,
        error,
        handleSignIn,
        handleSignOut,
        handleRefreshToken,
        handleError,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within a AuthProvider");
  }
  return context;
};
